import React, { Fragment, useEffect, useState } from "react";
import Terms from "./Terms";
import Loading from "./Loading";
import { gregorianToJalali } from "../utilities/dateConvertor";
import getInstallmentDetails from "../sevices/getInstallmentService";
import getPaymentLink from "../sevices/getPaymentLink";
import { IInstallmentResponse } from "../types/installment";
import { formatCurrency } from "../utilities/formatters";

// --- Constants ---
const installmentCounter = [
  "صفر", "اول", "دوم", "سوم", "چهارم", "پنجم", "ششم", "هفتم", "هشتم", "نهم", "دهم",
  "یازدهم", "دوازدهم", "سیزدهم", "چهاردهم", "پانزدهم", "شانزدهم", "هفدهم", "هجدهم",
  "نوزدهم", "بیستم", "بیست و یکم", "بیست و دوم", "بیست و سوم", "بیست و چهارم", "بیست و پنجم",
];

type PaymentMode = 'FULL' | 'CUSTOM';

export default function Content() {
  const [installment, setInstallment] = useState<IInstallmentResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('FULL');
  const [customAmount, setCustomAmount] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        await getInstallmentDetails(setInstallment);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- محاسبات ریاضی (Logic) ---
  
  const currentAmount = installment ? Number(installment.amount) : 0; 
  const penaltyVal = installment ? Number(installment.penalty_amount) : 0; 
  const remainingVal = installment && installment.remaining_amount ? Number(installment.remaining_amount) : 0; 
  
  // مبلغ خالص قسط
  const baseInstallmentAmount = currentAmount - penaltyVal;

  // مبلغ کل بدهی (برای نمایش)
  const totalPayable = currentAmount + remainingVal;

  // --- هندلر پرداخت (تغییر اصلی اینجاست) ---
  const handlePayment = () => {
    if (!installment?.id) return;

    setLoading(true);

    if (paymentMode === 'FULL') {
      // نکته کلیدی: وقتی پرداخت کامل هست، آرگومان دوم (amount) رو اصلا نمیفرستیم.
      // سرویس getPaymentLink وقتی آرگومان دوم نباشه، کوئری پارامتر amount رو نمیفرسته.
      // اینطوری بک‌اند میفهمه که این پرداخت کامل استاندارده و گیر قسط آخر نمیده.
      getPaymentLink(installment.id); 
    } else {
      // حالت پرداخت دلخواه
      const amountToSend = customAmount.replace(/,/g, '');
      
      if (!amountToSend || Number(amountToSend) <= 0) {
        alert("لطفا مبلغ معتبری وارد کنید");
        setLoading(false);
        return;
      }
      
      // اینجا مبلغ رو میفرستیم تا بک‌اند بدونه کاستومه
      getPaymentLink(installment.id, amountToSend);
    }
    
    // بازگرداندن دکمه به حالت عادی بعد از چند ثانیه
    setTimeout(() => setLoading(false), 3000);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, '');
    if (!isNaN(Number(rawValue))) {
      setCustomAmount(formatCurrency(rawValue));
    }
  };

  if (dataLoading || !installment) {
    return (
      <div className="flex flex-col justify-between items-center h-64 justify-center">
        <Loading />
        <h1 className="mt-4 text-secondaryText">در حال بارگزاری اطلاعات...</h1>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="flex flex-col">
        <h2 className="font-black text-lg">
          مشخصات قسط {installment.user.first_name} {installment.user.last_name}
        </h2>
        <p className="text-xs text-altText mt-3 mb-10">
          پرداخت سریع قسط به صورت آنلاین
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="flex flex-col items-center justify-center order-last lg:order-first">
            
            {/* شماره قسط */}
            <DetailRow 
              label="شماره قسط" 
              value={installmentCounter[installment.number] || installment.number} 
            />

            {/* مبلغ خالص قسط */}
            <DetailRow 
              label="مبلغ این قسط" 
              value={formatCurrency(baseInstallmentAmount)} 
              unit="تومان"
            />

            {/* مانده بدهی قبلی */}
            <DetailRow 
               label="مانده بدهی قبلی" 
               value={formatCurrency(remainingVal)} 
               unit="تومان"
               isWarning={remainingVal > 0} 
             />

            {/* مبلغ جریمه */}
            <DetailRow 
              label="مبلغ جریمه" 
              value={formatCurrency(penaltyVal)} 
              unit="تومان"
              isWarning={penaltyVal > 0}
            />

            {/* تاریخ سررسید */}
            <DetailRow 
              label="تاریخ سررسید" 
              value={gregorianToJalali(installment.due_date)} 
            />

            <div className="w-full border-t border-dashed border-borderColor my-4"></div>

            {/* بخش انتخاب روش پرداخت */}
            <div className="w-full mb-6">
                <p className="text-sm font-bold text-right mb-3 px-2">انتخاب مبلغ پرداخت:</p>
                
                {/* گزینه پرداخت کامل */}
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer mb-3 transition-all ${paymentMode === 'FULL' ? 'border-primary bg-primary/5' : 'border-borderColor'}`}>
                    <input 
                        type="radio" 
                        name="payment_method" 
                        className="form-radio text-primary w-4 h-4"
                        checked={paymentMode === 'FULL'}
                        onChange={() => setPaymentMode('FULL')}
                    />
                    <div className="mr-3 flex-1 flex justify-between items-center">
                        <span className="text-sm">پرداخت کل بدهی </span>
                        <span className="font-bold text-sm">{formatCurrency(totalPayable)} تومان</span>
                    </div>
                </label>

                {/* گزینه پرداخت دلخواه */}
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${paymentMode === 'CUSTOM' ? 'border-primary bg-primary/5' : 'border-borderColor'}`}>
                    <input 
                        type="radio" 
                        name="payment_method" 
                        className="form-radio text-primary w-4 h-4"
                        checked={paymentMode === 'CUSTOM'}
                        onChange={() => setPaymentMode('CUSTOM')}
                    />
                    <span className="mr-3 text-sm">پرداخت مبلغ دلخواه</span>
                </label>

                {/* اینپوت مبلغ دلخواه */}
                {paymentMode === 'CUSTOM' && (
                    <div className="mt-3 animate-fadeIn">
                        <div className="relative">
                            <input 
                                type="text" 
                                className="block w-full p-3 pr-4 text-sm text-gray-900 border border-borderColor rounded-lg focus:ring-primary focus:border-primary" 
                                placeholder="مبلغ مورد نظر را وارد کنید"
                                value={customAmount}
                                onChange={handleCustomAmountChange}
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <span className="text-gray-500 text-xs">تومان</span>
                            </div>
                        </div>
                        <p className="text-xs text-orange-500 mt-2 text-justify">
                            توجه: مبلغ وارد شده نمیتواند بیشتر از مبلغ کل بدهی باشد.
                        </p>
                    </div>
                )}
            </div>

            {/* دکمه پرداخت */}
            <div className="flex justify-end items-center w-full">
              <button
                className="lg:w-full w-full bg-primary flex justify-center text-center text-background py-3 rounded-md hover:bg-primaryHover transition-all duration-500 disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={handlePayment}
                disabled={loading || (paymentMode === 'CUSTOM' && !customAmount)}
              >
                {loading ? <Loading /> : `پرداخت آنلاین (${paymentMode === 'FULL' ? formatCurrency(totalPayable) : (customAmount || '0')} تومان)`}
              </button>
            </div>
          </div>
          <Terms />
        </div>
      </div>
    </Fragment>
  );
}

// --- Sub-component ---

interface DetailRowProps {
  label: string;
  value: string | number;
  unit?: string;
  isBold?: boolean;
  isWarning?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, unit, isBold = false, isWarning = false }) => {
  return (
    <div className="flex justify-end items-center w-full mb-4">
      <p className={`text-secondaryText text-xs text-left pl-6 w-5/12 ${isBold ? 'font-bold' : 'font-medium'}`}>
        {label}
      </p>
      <div className="w-7/12">
        <div className="flex rounded-lg shadow-sm w-full">
          <p
            className={`py-2 px-3 select-none block w-full shadow-sm text-center text-sm focus:z-10 border disabled:pointer-events-none 
            ${unit ? 'rounded-s-lg border-e-0' : 'rounded-lg'} 
            ${isBold ? 'font-bold' : ''}
            ${isWarning ? 'border-red-300 bg-red-50 text-red-600' : 'border-borderColor'}
            `}
          >
            {value}
          </p>
          {unit && (
            <span className={`px-4 inline-flex select-none items-center min-w-fit rounded-e-md border border-s-0 text-xs font-light 
                ${isWarning ? 'bg-red-100 border-red-300 text-red-600' : 'bg-gray-50 border-borderColor'}`}>
              {unit}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};